import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import GoogleWatchdogService from 'services/GoogleWatchdogService';
import useDialogSubmit from 'shared/hooks/useDialogSubmit';
import { ActionStep, ActionStepStatus, FIX_AD_OP, PERSON_STEP_OPS, TEST_AD_OP } from '../types';
import { parseTs } from '../logic';
import { DismissDialog } from './ActionDialogs';

const service = new GoogleWatchdogService();

/**
 * The changes an agent proposes for one card (itemId) or for no card at all (itemId null), each
 * with its reasons and an Approve and a Reject button. Approving a Google Ads step makes the
 * change at once, within the limits the API enforces; a site or other step goes back to the
 * agent, which does it and reports here. Shown only on the triage route.
 */
export default function ActionSteps({
  store,
  itemId,
  title = 'Action steps',
  onCards,
  onDialogChange,
}: {
  store: string;
  itemId: string | null;
  title?: string;
  /** On the list: also say how many steps wait on cards, with a link to each card. */
  onCards?: (itemId: string) => string;
  /** Told when an Approve or Reject dialog opens or closes, so the page can turn its hotkeys off. */
  onDialogChange: (open: boolean) => void;
}) {
  const location = useLocation();
  const [steps, setSteps] = useState<ActionStep[] | null>(null);
  const [error, setError] = useState('');
  // Only the latest load is shown: approving two steps quickly starts two, and they can land out of order.
  const loads = useRef(0);
  const load = useCallback(() => {
    const n = ++loads.current;
    service
      .listSteps(store)
      .then(all => {
        if (n !== loads.current) return;
        setSteps(all);
        setError('');
      })
      .catch(e => {
        if (n === loads.current) setError(e.message);
      });
  }, [store]);
  useEffect(() => {
    load();
  }, [load]);

  if (error)
    return (
      <Alert
        severity="error"
        sx={{ mt: 2 }}
        action={<Button onClick={load}>Retry</Button>}
      >
        Couldn't load the action steps: {error}
      </Alert>
    );
  if (!steps)
    return (
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 2, color: 'text.secondary' }}
      >
        Loading action steps…
      </Typography>
    );

  const mine = steps.filter(step => step.itemId === itemId);
  // Waiting first, in the order the agent proposed them (one step can lead into the next), then the
  // rest newest first: what needs a decision sits on top.
  const ordered = [
    ...mine.filter(s => s.status === 'waiting').reverse(),
    ...mine.filter(s => s.status !== 'waiting'),
  ];
  const waitingElsewhere = onCards
    ? steps.filter(s => s.itemId && s.status === 'waiting')
    : [];
  const cards = Array.from(
    new Set(waitingElsewhere.map(s => s.itemId as string)),
  );
  if (!ordered.length && !cards.length) return null;

  return (
    <Box
      component="section"
      aria-label={title}
      data-action-steps
      sx={{
        mt: 3,
        mb: 3,
        border: '1px solid #C7D2FE',
        borderRadius: '8px',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          px: 2,
          pt: 1.5,
          pb: 1,
          borderBottom: '1px solid #E0E7FF',
          bgcolor: '#EEF2FF',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#3730A3',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#4338CA' }}>
          {ordered.filter(s => s.status === 'waiting').length} waiting for you
        </Typography>
      </Box>
      {ordered.map(step => (
        <StepRow key={step.stepId} step={step} onChange={load} onDialogChange={onDialogChange} />
      ))}
      {cards.length > 0 && onCards && (
        <Typography
          variant="body2"
          sx={{
            px: 2,
            py: 1.25,
            color: '#334155',
            borderTop: ordered.length ? '1px solid #E2E8F0' : undefined,
          }}
        >
          {waitingElsewhere.length} more step
          {waitingElsewhere.length === 1 ? '' : 's'} waiting on {cards.length}{' '}
          card{cards.length === 1 ? '' : 's'}:{' '}
          {cards.map((card, index) => {
            const first = waitingElsewhere.find(s => s.itemId === card)!;
            return (
              <React.Fragment key={card}>
                {index > 0 && ' · '}
                {/* Carries where it came from, so Esc on the card returns to this tab (as the list's rows do). */}
                <Link component={RouterLink} to={{ pathname: onCards(card), state: { from: `${location.pathname}${location.search}` } }}>
                  {cardLabel(first)}
                </Link>
              </React.Fragment>
            );
          })}
        </Typography>
      )}
    </Box>
  );
}

const STATUS: Record<ActionStepStatus, { label: string; color: string }> = {
  waiting: { label: 'Waiting for you', color: '#4F46E5' },
  approved: { label: 'Approved', color: '#0369A1' },
  applied: { label: 'Done', color: '#047857' },
  validated: { label: 'Checked, not made', color: '#B45309' },
  done: { label: 'Done', color: '#047857' },
  failed: { label: 'Not made', color: '#DC2626' },
  rejected: { label: 'Rejected', color: '#64748B' },
  withdrawn: { label: 'Withdrawn', color: '#64748B' },
};

// A card's link names what the card is about: a keyword or ad group within its campaign, or the campaign.
const cardLabel = (step: ActionStep) => {
  const p = step.params;
  const within = p.keyword ?? p.adGroup;
  if (!p.campaign) return step.title;
  return within ? `"${String(within)}" in ${String(p.campaign)}` : String(p.campaign);
};
const isPersonStep = (step: ActionStep) => PERSON_STEP_OPS.includes(step.op);
const agentName = (actor: string) => actor.replace(/^agent:/, '');
// "Oct 28, 4:02 PM": a decision is read against the morning's numbers, so the time matters.
const when = (iso: string | null) =>
  iso ? parseTs(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

// One line on what happened to a decided step, in words.
function outcome(step: ActionStep): string | null {
  switch (step.status) {
    case 'applied':
      return `Made in Google Ads after ${step.decidedBy} approved it on ${when(step.decidedAt)}. ${step.note ?? ''}`.trim();
    case 'validated':
      return `${step.decidedBy} approved it. ${step.note ?? ''}`.trim();
    case 'failed':
      return `${step.decidedBy ? `${step.decidedBy} approved it, but it` : 'It'} was not made: ${step.note ?? 'no reason given'}`;
    case 'approved':
      return `${step.decidedBy} approved it on ${when(step.decidedAt)}. ${isPersonStep(step) ? `${step.note ?? `${agentName(step.proposedBy)} will do it.`} It reports back here.` : 'Being made now.'}`;
    case 'done':
      return `Done: ${step.note ?? ''}`;
    case 'rejected':
      return `${step.decidedBy} rejected it on ${when(step.decidedAt)}${step.note ? `: ${step.note}` : '.'}`;
    case 'withdrawn':
      return `${agentName(step.proposedBy)} took it back${step.note ? `: ${step.note}` : '.'}`;
    default:
      return null;
  }
}

function StepRow({
  step,
  onChange,
  onDialogChange,
}: {
  step: ActionStep;
  onChange: () => void;
  onDialogChange: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialogState] = useState<'approve' | 'reject' | null>(null);
  const setDialog = (next: 'approve' | 'reject' | null) => {
    setDialogState(next);
    onDialogChange(next !== null);
  };
  // A row can unmount with its dialog open (a failed reload shows the Alert instead): turn hotkeys back on.
  useEffect(() => {
    if (dialog) return () => onDialogChange(false);
  }, [dialog, onDialogChange]);
  const status = STATUS[step.status];
  const result = outcome(step);
  return (
    <Box sx={{ px: 2, py: 1.5, '& + &': { borderTop: '1px solid #E2E8F0' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: status.color,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {status.label}
          </Typography>
          <Typography
            sx={{ fontSize: '1rem', fontWeight: 700, overflowWrap: 'anywhere' }}
          >
            {step.title}
          </Typography>
        </Box>
        {step.status === 'waiting' && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, pt: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setDialog('approve')}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setDialog('reject')}
            >
              Reject
            </Button>
          </Box>
        )}
      </Box>
      {result && (
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            color: step.status === 'failed' ? '#B91C1C' : '#334155',
            maxWidth: '80ch',
          }}
        >
          {result}
          {step.result?.link && (
            <>
              {' '}
              <Link href={step.result.link} target="_blank" rel="noreferrer">
                Open
              </Link>
            </>
          )}
        </Typography>
      )}
      <Button
        size="small"
        aria-expanded={open}
        aria-controls={`step-why-${step.stepId}`}
        onClick={() => setOpen(value => !value)}
        sx={{ mt: 0.5, px: 0, minWidth: 0 }}
      >
        {open ? 'Hide why ▴' : 'Why this step ▾'}
      </Button>
      {open && <Why step={step} />}
      <ApproveDialog
        step={step}
        open={dialog === 'approve'}
        onClose={() => setDialog(null)}
        onDone={onChange}
      />
      <DismissDialog
        verb="Reject"
        label="Why not? (optional, the agent reads it)"
        headline={step.title}
        open={dialog === 'reject'}
        onClose={() => setDialog(null)}
        onDismiss={async reason => {
          // Reloaded on an error too: the step may have been decided elsewhere.
          try {
            await service.rejectStep(step.stepId, reason);
          } finally {
            onChange();
          }
        }}
      />
    </Box>
  );
}

function Why({ step }: { step: ActionStep }) {
  return (
    <Box
      id={`step-why-${step.stepId}`}
      sx={{
        mt: 0.5,
        p: 1.5,
        bgcolor: '#F8FAFC',
        borderRadius: 1,
        maxWidth: '80ch',
      }}
    >
      <Typography variant="body2">{step.why.summary}</Typography>
      {step.op === TEST_AD_OP && <TestAd step={step} />}
      {step.op === FIX_AD_OP && <FixAd step={step} />}
      {step.why.points.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {step.why.points.map((point, index) => (
            <Typography
              component="li"
              variant="body2"
              key={index}
              sx={{ mb: 0.25 }}
            >
              {point}
            </Typography>
          ))}
        </Box>
      )}
      {step.expected && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <b>What should happen:</b> {step.expected}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
      >
        Suggested by {agentName(step.proposedBy)} on {when(step.proposedAt)}
      </Typography>
    </Box>
  );
}

const textList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

// The new ad as it would run: every headline and description and where it sends people, beside the running ad's
// headlines when the agent sent them.
function TestAd({ step }: { step: ActionStep }) {
  const p = step.params;
  const running = textList(p.runningHeadlines);
  const paths = [p.path1, p.path2].filter(Boolean).map(String);
  const column = (label: string, headlines: string[], descriptions: string[] = []) => (
    <Box sx={{ flex: '1 1 260px', minWidth: 0, p: 1.25, bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="caption" component="div" sx={{ mt: 0.5, color: 'text.secondary' }}>
        Headlines ({headlines.length})
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {headlines.map((h, i) => (
          <Typography component="li" variant="body2" key={i}>
            {h}
          </Typography>
        ))}
      </Box>
      {descriptions.length > 0 && (
        <>
          <Typography variant="caption" component="div" sx={{ mt: 0.75, color: 'text.secondary' }}>
            Descriptions ({descriptions.length})
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {descriptions.map((d, i) => (
              <Typography component="li" variant="body2" key={i}>
                {d}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {column('New ad to test', textList(p.headlines), textList(p.descriptions))}
        {running.length > 0 && column('Running ad it is tested against', running)}
      </Box>
      <Typography variant="caption" component="div" sx={{ mt: 0.75, color: 'text.secondary', overflowWrap: 'anywhere' }}>
        Sends people to {String(p.finalUrl)}
        {paths.length > 0 && ` (shown as …/${paths.join('/')})`}. Runs beside the {textList(p.runningAdIds).length} ad
        {textList(p.runningAdIds).length === 1 ? '' : 's'} in {String(p.adGroup)}; nothing running is changed.
      </Typography>
    </Box>
  );
}

const FIX_KIND_LABELS: Record<string, string> = {
  outdated: 'Out of date',
  typo: 'Typo',
  wrong_claim: 'No longer true',
};

// A fix to a running ad: each line it changes, as it reads now and as it will read. Every other line stays.
function FixAd({ step }: { step: ActionStep }) {
  const p = step.params;
  const changes = Array.isArray(p.changes) ? (p.changes as { from?: unknown; to?: unknown }[]) : [];
  return (
    <Box sx={{ mt: 1, p: 1.25, bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
        Fix to the running ad{p.kind ? ` · ${FIX_KIND_LABELS[String(p.kind)] ?? String(p.kind)}` : ''}
      </Typography>
      {changes.map((c, i) => (
        <Box key={i} sx={{ mt: 0.75 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
            {String(c.from)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            → {String(c.to)}
          </Typography>
        </Box>
      ))}
      <Typography variant="caption" component="div" sx={{ mt: 0.75, color: 'text.secondary' }}>
        Ad {String(p.adId)} in {String(p.adGroup)}. Every other line stays as it is.
      </Typography>
    </Box>
  );
}

function ApproveDialog({
  step,
  open,
  onClose,
  onDone,
}: {
  step: ActionStep;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { busy, error, submit } = useDialogSubmit(onClose);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Approve: {step.title}</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">
          {isPersonStep(step)
            ? `${agentName(step.proposedBy)} starts on it as soon as you approve, and reports back on this card. A site change goes up for review before it is live.`
            : step.op === TEST_AD_OP
              ? `This creates the new ad in ${String(step.params.adGroup)} as soon as you approve, running beside the ads already there. Nothing running is changed. Google reviews a new ad before it shows, usually within a day.`
              : step.op === FIX_AD_OP
                ? `This changes only the lines shown in the ad in ${String(step.params.adGroup)}, as soon as you approve. Google reviews the edited ad again, usually within a day.`
                : 'This changes Google Ads as soon as you approve. The limits are checked first; if the account has moved since the step was written, nothing is changed and the card says why.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() =>
            submit(async () => {
              // Reloaded on an error too: the step may have been decided elsewhere, or made despite a timeout.
              try {
                await service.approveStep(step.stepId);
              } finally {
                onDone();
              }
            })
          }
        >
          {busy ? 'Making it…' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
